// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0
/**
 * @file
 * Some helper functions to check the type of variables. It's a workaround as I wasn't able to get Svelte 3 to work
 * with TypeScript.
 */

/**
 * Convenience helper for string checks.
 *
 * @param {any} value - The value to check
 * @param {string} name - Name of the variable for error messages
 */
export function assertString(value, name) {
  if (typeof value !== 'string') {
    throw new TypeError(`Invalid type for '${name}': expected 'string', got ${typeof value}`);
  }
}

/**
 * Convenience helper for boolean checks.
 *
 * @param {any} value - The value to check
 * @param {string} name - Name of the variable for error messages
 */
export function assertBoolean(value, name) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`Invalid type for '${name}': expected 'boolean', got ${typeof value}`);
  }
}

/**
 * Checks if a value is an instance of a specific class or objectConstructor.
 *
 * @param {any} value - The value to check
 * @param {Function} objectConstructor - The expected objectConstructor (e.g., Array, Date, CustomClass)
 * @param {string} [name] - Optional name for clearer error messages
 */
export function assertInstanceOf(value, objectConstructor, name = 'value') {
  if (!(value instanceof objectConstructor)) {
    throw new TypeError(
      `Invalid instance for '${name}': expected ${objectConstructor.name}, got ${value?.objectConstructor?.name}`,
    );
  }
}

export function assertArray(value, name = 'value') {
  if (!Array.isArray(value)) {
    throw new TypeError(`Invalid type for '${name}': expected Array, got ${typeof value}`);
  }
}

/**
 * Checks if a value is a plain object (a "dictionary" in the Python sense).
 * Rejects arrays, null, and class instances.
 *
 * @param {any} value - The value to check
 * @param {string} [name] - Optional name for clearer error messages
 */
export function assertDictionary(value, name = 'value') {
  const isPlainObject =
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype;

  if (!isPlainObject) {
    throw new TypeError(
      `Invalid type for '${name}': expected plain object (dictionary), got ${Object.prototype.toString.call(value)}`,
    );
  }
}
